import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  useToast,
  Spinner,
  Center
} from '@chakra-ui/react';
import { Match } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { getDoc, doc, addDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface QuinielaFormProps {
  matches: Match[];
  onSubmit: (predictions: Record<string, { score_a: number; score_b: number }>) => void;
  isLoading?: boolean;
  teamLogos?: { [teamName: string]: string };
  userPredictions: { match_id: string; predicted_score_a: number; predicted_score_b: number }[];
}

export const QuinielaForm: React.FC<QuinielaFormProps> = ({ matches, onSubmit, isLoading, teamLogos, userPredictions }) => {
  const [predictions, setPredictions] = React.useState<Record<string, { score_a: number; score_b: number }>>({});
  const { user } = useAuth();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  if (isLoading) {
    return (
      <Center p={8}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!matches.length) {
    return (
      <Center p={8}>
        <Text>No hay partidos disponibles en este momento.</Text>
      </Center>
    );
  }

  const handleScoreChange = (matchId: string, team: 'a' | 'b', value: number) => {
    setPredictions(prev => ({
      ...prev,
      [matchId]: {
        score_a: team === 'a' ? value : (prev[matchId]?.score_a || 0),
        score_b: team === 'b' ? value : (prev[matchId]?.score_b || 0)
      }
    }));
  };

  const handleSubmit = async (values: any) => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'Debes iniciar sesión para guardar tus predicciones',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Prevenir doble envío
    if (isSubmitting) {
      return;
    }

    // Filtra solo los partidos que aún pueden ser pronosticados
    const matchesToPredict = matches.filter(
      match => match.score_a === null && match.score_b === null
    );

    const allPredicted = matchesToPredict.every(match => 
      values[match.id]?.score_a !== undefined && 
      values[match.id]?.score_b !== undefined
    );

    if (!allPredicted) {
      toast({
        title: 'Error',
        description: 'Debes predecir todos los partidos pendientes',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Solo guardar predicciones para partidos disponibles
      const predictions = matches
        .filter(match => match.score_a === null && match.score_b === null)
        .map(match => ({
          match_id: match.id,
          user_id: user.uid,
          predicted_score_a: values[match.id]?.score_a,
          predicted_score_b: values[match.id]?.score_b,
          activo: 0
        }));

      for (const prediction of predictions) {
        // Elimina predicción anterior si existe
        const q = query(
          collection(db, 'predictions'),
          where('user_id', '==', user.uid),
          where('match_id', '==', prediction.match_id)
        );
        const existing = await getDocs(q);
        for (const docSnap of existing.docs) {
          await deleteDoc(doc(db, 'predictions', docSnap.id));
        }
        // Guarda la nueva
        await addDoc(collection(db, 'predictions'), prediction);
      }

      toast({
        title: 'Éxito',
        description: 'Tus predicciones han sido guardadas',
        status: 'success',
        duration: 3000,
      });

      // Quita o comenta esta línea para evitar doble notificación
      // onSubmit(values);
    } catch (error) {
      console.error('Error guardando predicciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron guardar tus predicciones',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const matchesByPhase = matches.reduce((acc, match) => {
    if (!acc[match.phase]) {
      acc[match.phase] = [];
    }
    acc[match.phase].push(match);
    return acc;
  }, {} as Record<string, Match[]>);

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(predictions);
    }}>
      <VStack spacing={8} align="stretch">
        {Object.entries(matchesByPhase).map(([phase, phaseMatches]) => (
          <Box key={phase}>
            <Heading size="md" mb={4}>{phase}</Heading>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Local</Th>
                  <Th>Marcador</Th>
                  <Th>Visitante</Th>
                </Tr>
              </Thead>
              <Tbody>
                {phaseMatches.map((match) => {
                  const userPrediction = userPredictions.find(p => p.match_id === match.id);
                  const isDisabled = (match.score_a !== null && match.score_b !== null) || !!userPrediction;

                  return (
                    <Tr key={match.id}>
                      <Td>
                        <Box display="flex" alignItems="center">
                          <Image
                            src={teamLogos?.[match.team_a || ''] || '/team-placeholder.png'}
                            alt={match.team_a}
                            boxSize="40px"
                            mr={2}
                          />
                          <Text>{match.team_a}</Text>
                        </Box>
                      </Td>
                      <Td>
                        <Box display="flex" alignItems="center" gap={2}>
                          <NumberInput
                            min={0}
                            max={99}
                            value={userPrediction ? userPrediction.predicted_score_a : predictions[match.id]?.score_a || 0}
                            onChange={(_, value) => handleScoreChange(match.id, 'a', value)}
                            isDisabled={isDisabled}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <Text>-</Text>
                          <NumberInput
                            min={0}
                            max={99}
                            value={userPrediction ? userPrediction.predicted_score_b : predictions[match.id]?.score_b || 0}
                            onChange={(_, value) => handleScoreChange(match.id, 'b', value)}
                            isDisabled={isDisabled}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </Box>
                      </Td>
                      <Td>
                        <Box display="flex" alignItems="center">
                          <Image
                            src={teamLogos?.[match.team_b || ''] || '/team-placeholder.png'}
                            alt={match.team_b || 'Por definir'}
                            boxSize="40px"
                            mr={2}
                          /> 
                          <Text>{match.team_b || 'Por definir'}</Text>
                        </Box>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        ))}
        <Button
          type="submit"
          colorScheme="blue"
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
        >
          Guardar Predicciones
        </Button>
      </VStack>
    </form>
  );
}; 